'use client'

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { getApiUrl } from './api-url'
import { fetchSharedProjectData } from './synoptic-storage'

interface SharedProject {
  id: string
  name: string
  data: Record<string, unknown>
  author_name?: string
  created_at: string
  updated_at: string
}

interface ReadOnlyContextType {
  readOnly: boolean
  sharedProject: SharedProject | null
  isLoading: boolean
  error: string | null
}

const ReadOnlyContext = createContext<ReadOnlyContextType>({
  readOnly: false,
  sharedProject: null,
  isLoading: false,
  error: null
})

export function useReadOnly() {
  return useContext(ReadOnlyContext)
}

interface ReadOnlyProviderProps {
  children: ReactNode
  shareToken: string
}

export function ReadOnlyProvider({ children, shareToken }: ReadOnlyProviderProps) {
  const [sharedProject, setSharedProject] = useState<SharedProject | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!shareToken) {
      setIsLoading(false)
      setError('NO_TOKEN')
      return
    }

    const fetchSharedProject = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const response = await fetch(getApiUrl(`/api/synoptic/share/${shareToken}`))

        if (!response.ok) {
          if (response.status === 404) {
            setError('NOT_FOUND')
          } else if (response.status === 403) {
            setError('NOT_PUBLIC')
          } else {
            setError('FETCH_ERROR')
          }
          return
        }

        const { project } = await response.json()

        // Projet migré vers Supabase Storage : l'API fournit une URL signée
        // à la place du jsonb inline → télécharger et décoder le blob
        if (project?.data_url && !project.data) {
          try {
            const data = await fetchSharedProjectData(project.data_url)
            project.data = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>
          } catch {
            setError('FETCH_ERROR')
            return
          }
        }

        setSharedProject(project)
      } catch (err) {
        setError('NETWORK_ERROR')
      } finally {
        setIsLoading(false)
      }
    }

    fetchSharedProject()
  }, [shareToken])

  return (
    <ReadOnlyContext.Provider value={{
      readOnly: true,
      sharedProject,
      isLoading,
      error
    }}>
      {children}
    </ReadOnlyContext.Provider>
  )
}
